
///
/// EarthBrowser - Dataset Browser
///
/// Fast table-based browser for large datasets using TanStack Table
/// Copyright (c) 2025 Geodata Labs
///

import { Button } from "@/components/ui/button"
import { cn } from "@/utils/utils"
import {
	flexRender,
	getCoreRowModel,
	getFilteredRowModel,
	getSortedRowModel,
	useReactTable,
	type ColumnDef,
	type SortingState,
	type ColumnFiltersState,
} from "@tanstack/react-table"
import {
	Search,
	X,
	ArrowUpDown,
	ArrowUp,
	ArrowDown,
	Loader2,
	ChevronLeft,
	ChevronRight,
	ChevronsLeft,
	ChevronsRight,
	Database,
	Download
} from "lucide-react"
import { useCallback, useEffect, useRef, useState, useMemo } from "react"

export interface DatasetBrowserColumn<T> {
	id: string
	header: string
	accessorKey?: keyof T
	accessorFn?: (row: T) => unknown
	cell?: (value: unknown, row: T) => React.ReactNode
	sortable?: boolean
	width?: number
	minWidth?: number
}

export interface PaginationProps {
	currentPage: number
	totalPages: number
	hasNextPage: boolean
	hasPrevPage: boolean
	onNextPage: () => void
	onPrevPage: () => void
	onFirstPage: () => void
	onLastPage: () => void
}

export interface ResultsInfoProps {
	currentCount: number
	totalCount: number
	isSearchMode?: boolean
}

export interface DatasetBrowserProps<T> {
	isOpen: boolean
	onClose: () => void
	title: string
	data: T[]
	columns: DatasetBrowserColumn<T>[]
	onRowClick: (row: T) => void
	searchPlaceholder?: string
	isLoading?: boolean
	icon?: React.ReactNode
	iconColor?: string
	className?: string
	globalFilterFn?: (row: T, query: string) => boolean
	onSearchChange?: (query: string) => void
	content?: React.ReactNode
	pagination?: PaginationProps
	resultsInfo?: ResultsInfoProps
	closeOnRowClick?: boolean // Whether to close the browser when a row is clicked (default: true)
	onExport?: () => void // Optional export handler
	headerActions?: React.ReactNode // Optional custom action buttons in header
	showKeyboardHints?: boolean // Whether to show keyboard hints in footer (default: true)
	// Movable mode - disables backdrop and centers positioning (for use with MovableDatasetBrowser wrapper)
	disableBackdrop?: boolean
	disablePositioning?: boolean
	// Renders as full-bleed panel: no backdrop, no dialog positioning, toolbar without title/close (for DatasetBrowserWindow)
	embeddedInWindow?: boolean
	// Minimum length before onSearchChange fires (default 3; locations browser uses 2)
	searchNotifyMinLength?: number
}

export function DatasetBrowser<T extends Record<string, unknown>>({
	isOpen,
	onClose,
	title,
	data,
	columns,
	onRowClick,
	searchPlaceholder = "Search...",
	isLoading = false,
	icon,
	iconColor = "text-cyan-400",
	className,
	onSearchChange,
	content,
	pagination,
	resultsInfo,
	closeOnRowClick = true,
	onExport,
	headerActions,
	showKeyboardHints = true,
	disableBackdrop = false,
	disablePositioning = false,
	embeddedInWindow = false,
	searchNotifyMinLength = 3
}: DatasetBrowserProps<T>) {
	const effectiveDisableBackdrop = disableBackdrop || embeddedInWindow
	const effectiveDisablePositioning = disablePositioning || embeddedInWindow
	const suppressEscapeClose = embeddedInWindow
	const [searchQuery, setSearchQuery] = useState('')
	const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')

	// Debounce search query (300ms delay, only notify parent past min length)
	useEffect(() => {
		const timer = setTimeout(() => {
			if (searchQuery.length === 0 || searchQuery.length >= searchNotifyMinLength) {
				setDebouncedSearchQuery(searchQuery)
				if (onSearchChange) {
					onSearchChange(searchQuery)
				}
			}
		}, 300)

		return () => clearTimeout(timer)
	}, [searchQuery, onSearchChange, searchNotifyMinLength])

	// Handle search changes (updates immediately in UI, but filtering is debounced)
	const handleSearchChange = useCallback((value: string) => {
		setSearchQuery(value)
	}, [])

	const [sorting, setSorting] = useState<SortingState>([])
	const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
	const [selectedRowIndex, setSelectedRowIndex] = useState(0)
	const inputRef = useRef<HTMLInputElement>(null)
	const tableContainerRef = useRef<HTMLDivElement>(null)

	// Convert custom columns to TanStack Table column definitions
	const tableColumns = useMemo<ColumnDef<T>[]>(() => {
		return columns.map(col => ({
			id: col.id,
			accessorKey: col.accessorKey as string,
			accessorFn: col.accessorFn,
			header: ({ column }) => {
				if (!col.sortable) return <div>{col.header}</div>

				return (
					<Button
						variant="ghost"
						size="sm"
						className="h-8 px-2 hover:bg-white/10"
						onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
					>
						{col.header}
						{column.getIsSorted() === "asc" ? (
							<ArrowUp className="ml-2 h-3 w-3" />
						) : column.getIsSorted() === "desc" ? (
							<ArrowDown className="ml-2 h-3 w-3" />
						) : (
							<ArrowUpDown className="ml-2 h-3 w-3 opacity-50" />
						)}
					</Button>
				)
			},
			cell: ({ getValue, row }) => {
				const value = getValue()
				return col.cell ? col.cell(value, row.original) : value
			},
			size: col.width,
			minSize: col.minWidth || 50,
			enableSorting: col.sortable !== false,
		}))
	}, [columns])

	// Filter and sort data based on search query (only if no external search handler)
	// Prioritizes: 1) Name starts with query, 2) Name contains query, 3) Other fields contain query
	const filteredData = useMemo(() => {
		if (!debouncedSearchQuery || onSearchChange) return data

		const query = debouncedSearchQuery.toLowerCase()

		// Only search if 3+ characters
		if (query.length < 3) return data

		// Pre-compute name field for performance - check top-level and nested 'properties'
		const nameFields = ['name', 'title', 'location', 'place', 'object_name']
		const getNameValue = (row: T): string => {
			// Check top-level fields
			for (const field of nameFields) {
				if (field in row) {
					const value = row[field as keyof T]
					if (typeof value === 'string') {
						return (value as string).toLowerCase()
					}
				}
			}
			// Check nested 'properties' object (for GeoJSON Features)
			if ('properties' in row && typeof row.properties === 'object' && row.properties !== null) {
				const props = row.properties as Record<string, unknown>
				for (const field of nameFields) {
					if (field in props) {
						const value = props[field]
						if (typeof value === 'string') {
							return (value).toLowerCase()
						}
					}
				}
			}
			return ''
		}

		// Recursively search for string/number matches in nested objects
		const searchInObject = (obj: unknown, query: string): boolean => {
			if (typeof obj === 'string') {
				return obj.toLowerCase().includes(query)
			}
			if (typeof obj === 'number') {
				return obj.toString().includes(query)
			}
			if (typeof obj === 'object' && obj !== null) {
				for (const value of Object.values(obj)) {
					if (searchInObject(value, query)) return true
				}
			}
			return false
		}

		// Filter and score matches in one pass
		const scoredMatches: Array<{row: T, score: number, nameIndex: number}> = []

		for (const row of data) {
			const name = getNameValue(row)

			// Check name field first (most common case)
			if (name) {
				const nameIndex = name.indexOf(query)
				if (nameIndex !== -1) {
					// Score: 0 = starts with (best), 1-99 = position in name, 100 = not in name
					const score = nameIndex === 0 ? 0 : nameIndex
					scoredMatches.push({ row, score, nameIndex })
					continue
				}
			}

			// Check all fields recursively (handles nested objects like GeoJSON properties)
			if (searchInObject(row, query)) {
				scoredMatches.push({ row, score: 100, nameIndex: -1 })
			}
		}

		// Sort by score (lower is better), then by original order
		scoredMatches.sort((a, b) => a.score - b.score)

		// Return only rows, limit to 200 results for performance
		return scoredMatches.slice(0, 200).map(m => m.row)
	}, [data, debouncedSearchQuery, onSearchChange])

	const table = useReactTable({
		data: filteredData,
		columns: tableColumns,
		state: {
			sorting,
			columnFilters,
		},
		onSortingChange: setSorting,
		onColumnFiltersChange: setColumnFilters,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
	})

	// Reset selection when data changes
	useEffect(() => {
		setSelectedRowIndex(0)
	}, [filteredData])

	// Focus input when opened
	useEffect(() => {
		if (isOpen) {
			inputRef.current?.focus()
			setSearchQuery('')
			setSelectedRowIndex(0)
			setSorting([])
		}
	}, [isOpen])

	// Keyboard navigation
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (!isOpen) return

			const rows = table.getRowModel().rows

			switch (e.key) {
				case 'ArrowDown':
					e.preventDefault()
					setSelectedRowIndex(prev => Math.min(prev + 1, rows.length - 1))
					break
				case 'ArrowUp':
					e.preventDefault()
					setSelectedRowIndex(prev => Math.max(prev - 1, 0))
					break
				case 'Enter':
					e.preventDefault()
					if (rows[selectedRowIndex]) {
						onRowClick(rows[selectedRowIndex].original)
						if (closeOnRowClick) {
							onClose()
						}
					}
					break
				case 'Escape':
					if (suppressEscapeClose) {
						return
					}
					e.preventDefault()
					onClose()
					break
			}
		}

		document.addEventListener('keydown', handleKeyDown)
		return () => document.removeEventListener('keydown', handleKeyDown)
	// eslint-disable-next-line react-hooks/exhaustive-deps -- table is a new object every render, rows accessed via closure
	}, [isOpen, selectedRowIndex, onRowClick, onClose, closeOnRowClick, suppressEscapeClose])

	// Scroll selected row into view
	useEffect(() => {
		if (!tableContainerRef.current) return
		const selectedRow = tableContainerRef.current.querySelector(`[data-row-index="${selectedRowIndex}"]`)
		selectedRow?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
	}, [selectedRowIndex])

	const handleRowClick = useCallback((row: T) => {
		onRowClick(row)
		if (closeOnRowClick) {
			onClose()
		}
	}, [onRowClick, onClose, closeOnRowClick])

	if (!isOpen) return null

	const rows = table.getRowModel().rows

	return (
		<>
			{/* Backdrop */}
			{!effectiveDisableBackdrop && (
				<div
					className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 animate-in fade-in duration-300"
					onClick={onClose}
				/>
			)}

			{/* Browser Dialog */}
			<div
				className={cn(
					!effectiveDisablePositioning && "fixed z-50 animate-in fade-in slide-in-from-top-4 duration-300",
					!effectiveDisablePositioning && "left-1/2 -translate-x-1/2 top-[10vh]",
					!effectiveDisablePositioning && "w-[calc(100vw-2rem)] max-w-[900px]",
					effectiveDisablePositioning && "w-full h-full",
					!embeddedInWindow && "rounded-2xl",
					"bg-black/90 backdrop-blur-xl border border-white/20 shadow-2xl",
					!effectiveDisablePositioning && "max-h-[80vh]",
					"flex flex-col overflow-hidden",
					className
				)}
			>
				{/* Gradient glow effect */}
				{!effectiveDisablePositioning && (
					<div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-cyan-500/10 to-teal-500/10 pointer-events-none rounded-2xl" />
				)}

				{/* Embedded toolbar (search + counts) when inside DatasetBrowserWindow */}
				{embeddedInWindow && (
					<div className="relative z-10 p-3 border-b border-white/10 shrink-0">
						<div className="flex items-center gap-2 mb-2 flex-wrap">
							{resultsInfo?.currentCount !== undefined && (
								<div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/5 border border-white/10">
									<Database className="w-3 h-3 text-cyan-400/70" />
									<span className="text-[10px] font-medium text-white/70">
										{isLoading ? (
											<span className="text-white/40">...</span>
										) : resultsInfo.isSearchMode ? (
											<>
												<span className="text-cyan-400">{resultsInfo.currentCount.toLocaleString()}</span>
												<span className="text-white/40"> results</span>
											</>
										) : (
											<>
												<span className="text-cyan-400">{resultsInfo.currentCount.toLocaleString()}</span>
												<span className="text-white/40"> / {(resultsInfo.totalCount ?? resultsInfo.currentCount).toLocaleString()}</span>
											</>
										)}
									</span>
								</div>
							)}
							{onExport && (
								<Button
									variant="ghost"
									size="sm"
									onClick={onExport}
									className="text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10 h-8 px-3 rounded-full"
									disabled={isLoading || data.length === 0}
								>
									<Download className="w-4 h-4 mr-1.5" />
									<span className="text-xs font-medium">Export</span>
								</Button>
							)}
							{headerActions}
						</div>
						{content && (
							<div className="mb-2">
								{content}
							</div>
						)}
						<div className="flex items-center gap-2 bg-white/5 rounded px-2 py-1.5">
							<Search className="w-3.5 h-3.5 text-cyan-400 flex-shrink-0" />
							<input
								ref={inputRef}
								type="text"
								autoComplete="off"
								placeholder={searchPlaceholder}
								value={searchQuery}
								onChange={(e) => handleSearchChange(e.target.value)}
								className="flex-1 bg-transparent text-white placeholder:text-white/40 focus:outline-none text-xs"
							/>
							{searchQuery.length > 0 && searchQuery.length < searchNotifyMinLength && (
								<span className="text-[10px] text-white/40 flex-shrink-0">Type {searchNotifyMinLength - searchQuery.length} more...</span>
							)}
							{isLoading && (
								<Loader2 className="w-3.5 h-3.5 text-cyan-400 animate-spin flex-shrink-0" />
							)}
						</div>
					</div>
				)}

				{/* Header - dialog mode only */}
				{!effectiveDisablePositioning && (
					<div className="relative z-10 p-3 border-b border-white/10">
					{/* Title Bar */}
					<div className="flex items-center justify-between mb-2">
						<div className="flex items-center gap-2">
							{icon && <div className={cn(iconColor, "scale-75")}>{icon}</div>}
							<h2 className="text-sm font-medium text-white">{title}</h2>
							{/* Results indicator - compact */}
							{resultsInfo?.currentCount !== undefined && (
								<div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/5 border border-white/10">
									<Database className="w-3 h-3 text-cyan-400/70" />
									<span className="text-[10px] font-medium text-white/70">
										{isLoading ? (
											<span className="text-white/40">...</span>
										) : resultsInfo.isSearchMode ? (
											<>
												<span className="text-cyan-400">{resultsInfo.currentCount.toLocaleString()}</span>
												<span className="text-white/40"> results</span>
											</>
										) : (
											<>
												<span className="text-cyan-400">{resultsInfo.currentCount.toLocaleString()}</span>
												<span className="text-white/40"> / {(resultsInfo.totalCount ?? resultsInfo.currentCount).toLocaleString()}</span>
											</>
										)}
									</span>
								</div>
							)}
							{/* Export button */}
							{onExport && (
								<Button
									variant="ghost"
									size="sm"
									onClick={onExport}
									className="text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10 h-8 px-3 rounded-full"
									disabled={isLoading || data.length === 0}
								>
									<Download className="w-4 h-4 mr-1.5" />
									<span className="text-xs font-medium">Export</span>
								</Button>
							)}
						</div>
						<div className="flex items-center gap-2">
							{/* Custom header actions */}
							{headerActions}
							<Button
								variant="ghost"
								size="sm"
								onClick={onClose}
								className="h-8 w-8 p-0 hover:bg-white/10"
							>
								<X className="w-4 h-4" />
							</Button>
						</div>
					</div>

					{/* Optional Content Section - compact */}
					{content && (
						<div className="mb-2">
							{content}
						</div>
					)}

					{/* Search Bar - compact */}
					<div className="flex items-center gap-2 bg-white/5 rounded px-2 py-1.5">
						<Search className="w-3.5 h-3.5 text-cyan-400 flex-shrink-0" />
						<input
							ref={inputRef}
							type="text"
							autoComplete="off"
							placeholder={searchPlaceholder}
							value={searchQuery}
							onChange={(e) => handleSearchChange(e.target.value)}
							className="flex-1 bg-transparent text-white placeholder:text-white/40 focus:outline-none text-xs"
						/>
						{searchQuery.length > 0 && searchQuery.length < searchNotifyMinLength && (
							<span className="text-[10px] text-white/40 flex-shrink-0">Type {searchNotifyMinLength - searchQuery.length} more...</span>
						)}
						{isLoading && (
							<Loader2 className="w-3.5 h-3.5 text-cyan-400 animate-spin flex-shrink-0" />
						)}
					</div>
				</div>
				)}

				{/* Table */}
				<div
					ref={tableContainerRef}
					className="relative z-10 flex-1 overflow-auto"
				>
					{rows.length === 0 ? (
						<div className="flex flex-col items-center justify-center py-12 text-white/40">
							<Search className="w-12 h-12 mb-3 opacity-20" />
							<p className="text-sm">No results found</p>
						</div>
					) : (
						<table className="w-full">
							<thead className="sticky top-0 bg-black/80 backdrop-blur-sm z-10">
								{table.getHeaderGroups().map(headerGroup => (
									<tr key={headerGroup.id} className="border-b border-white/10">
										{headerGroup.headers.map(header => (
											<th
												key={header.id}
												className="text-left px-3 py-1.5 text-[11px] font-semibold text-cyan-400/80 uppercase tracking-wide"
												style={{ width: header.getSize() }}
											>
												{header.isPlaceholder
													? null
													: flexRender(
														header.column.columnDef.header,
														header.getContext()
													)}
											</th>
										))}
									</tr>
								))}
							</thead>
							<tbody>
								{rows.map((row, idx) => {
									const isSelected = idx === selectedRowIndex
									return (
										<tr
											key={row.id}
											data-row-index={idx}
											onClick={() => handleRowClick(row.original)}
											className={cn(
												"border-b border-white/5 cursor-pointer transition-colors",
												"hover:bg-white/10",
												isSelected && "bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border-y border-cyan-400/30"
											)}
										>
											{row.getVisibleCells().map(cell => (
												<td
													key={cell.id}
													className="px-3 py-1.5 text-xs text-white/90"
												>
													{flexRender(
														cell.column.columnDef.cell,
														cell.getContext()
													)}
												</td>
											))}
										</tr>
									)
								})}
							</tbody>
						</table>
					)}
				</div>

				{/* Footer - Pagination and Keyboard hints - compact */}
				<div className="relative z-10 px-3 py-2 border-t border-white/10 flex items-center justify-between">
					{/* Pagination Controls - compact */}
					{pagination && (
						<div className="flex items-center gap-1">
							<Button
								variant="ghost"
								size="sm"
								onClick={pagination.onFirstPage}
								disabled={!pagination.hasPrevPage || isLoading}
								className="h-6 w-6 p-0 hover:bg-white/10 disabled:opacity-30"
								title="First page"
							>
								<ChevronsLeft className="w-3 h-3" />
							</Button>
							<Button
								variant="ghost"
								size="sm"
								onClick={pagination.onPrevPage}
								disabled={!pagination.hasPrevPage || isLoading}
								className="h-6 w-6 p-0 hover:bg-white/10 disabled:opacity-30"
								title="Previous page"
							>
								<ChevronLeft className="w-3 h-3" />
							</Button>
							<span className="text-[10px] text-white/60 px-2 min-w-[80px] text-center">
								Page {pagination.currentPage + 1} of {pagination.totalPages || 1}
							</span>
							<Button
								variant="ghost"
								size="sm"
								onClick={pagination.onNextPage}
								disabled={!pagination.hasNextPage || isLoading}
								className="h-6 w-6 p-0 hover:bg-white/10 disabled:opacity-30"
								title="Next page"
							>
								<ChevronRight className="w-3 h-3" />
							</Button>
							<Button
								variant="ghost"
								size="sm"
								onClick={pagination.onLastPage}
								disabled={!pagination.hasNextPage || isLoading}
								className="h-6 w-6 p-0 hover:bg-white/10 disabled:opacity-30"
								title="Last page"
							>
								<ChevronsRight className="w-3 h-3" />
							</Button>
						</div>
					)}

					{/* Keyboard hints - compact */}
					{showKeyboardHints && (
						<div className="hidden md:flex gap-3 text-[10px] text-white/40 ml-auto">
							<span className="flex items-center gap-1">
								<kbd className="px-1 py-0.5 bg-white/10 rounded border border-white/20 text-[10px]">↑</kbd>
								<kbd className="px-1 py-0.5 bg-white/10 rounded border border-white/20 text-[10px]">↓</kbd>
								Navigate
							</span>
							<span className="flex items-center gap-1">
								<kbd className="px-1 py-0.5 bg-white/10 rounded border border-white/20 text-[10px]">↵</kbd>
								Select
							</span>
							{!embeddedInWindow && (
								<span className="flex items-center gap-1">
									<kbd className="px-1 py-0.5 bg-white/10 rounded border border-white/20 text-[10px]">Esc</kbd>
									Close
								</span>
							)}
						</div>
					)}
				</div>
			</div>
		</>
	)
}
